// ---------- units & stores ----------

/** Units an author may write in seed data. */
export type Unit = 'g' | 'kg' | 'ml' | 'l' | 'each';

/** Units quantities are normalised to before aggregation. */
export type BaseUnit = 'g' | 'ml' | 'each';

export type Dimension = 'mass' | 'volume' | 'count';

export type Store = 'aldi' | 'tesco' | 'lidl';

export const STORES: readonly Store[] = ['aldi', 'tesco', 'lidl'] as const;

/** Coarse shelf grouping. Each store orders these differently. */
export type AisleCategory =
  | 'produce'
  | 'meat_fish'
  | 'dairy_chilled'
  | 'bakery'
  | 'ambient'
  | 'frozen'
  | 'drinks'
  | 'household';

export type Weekday = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

export const WEEKDAYS: readonly Weekday[] = [
  'mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun',
] as const;

// ---------- reference data ----------

export interface Ingredient {
  id: string;
  name: string;
  aisle: AisleCategory;
  baseUnit: BaseUnit;
  /** Only staples are eligible for the cupboard-check exclusion. */
  isCupboardStaple: boolean;
  /** Alternate names an AI suggestion or user might use for this ingredient. */
  aliases: string[];
}

export interface PackSize {
  ingredientId: string;
  store: Store;
  packQty: number;
  unit: Unit;
  /** Shelf-facing text, e.g. "1kg pack". */
  label: string;
}

// ---------- meals ----------

export type MealTag = 'budget' | 'batch' | 'quick' | 'veggie' | 'mild' | 'freezer';

export type CostBand = 'budget' | 'mid' | 'treat';

export interface MealIngredient {
  ingredientId: string;
  /** Quantity for ONE adult portion. Never a whole-meal quantity. */
  qtyPerPortion: number;
  unit: Unit;
  isOptional: boolean;
  prepNote: string | null;
}

export interface Meal {
  id: string;
  name: string;
  description: string;
  cuisine: string;
  mainProtein: string;
  effortMinutes: number;
  costBand: CostBand;
  tags: MealTag[];
  ingredients: MealIngredient[];
  method: string[];
  extras?: ExtraItem[];
}

/**
 * A recipe line that is not in the ingredient catalogue — gochujang, a jar of
 * pesto. It still has to reach the list, but it cannot aggregate with the
 * catalogue or be pack-rounded, so it travels separately from `ingredients`.
 */
export interface ExtraItem {
  label: string;
  /** Per ONE adult portion, like every other quantity. Null means "some". */
  qtyPerPortion: number | null;
  /** Free text shown on the list: "tbsp", "jar". Empty when qty is null. */
  unitLabel: string;
  aisle: AisleCategory;
}

export type LunchComponentType = 'base' | 'filling' | 'side' | 'snack' | 'drink';

export interface LunchComponent {
  id: string;
  name: string;
  type: LunchComponentType;
  ingredientId: string;
  /** Quantity for ONE lunch box, regardless of who eats it. */
  qtyPerLunch: number;
  unit: Unit;
}

// ---------- household ----------

export type PersonType = 'adult' | 'child';

export interface Person {
  id: string;
  name: string;
  personType: PersonType;
  /** Share of one adult portion. */
  portionFactor: number;
  /** Days this person needs a packed lunch. Empty for most people. */
  lunchDays: Weekday[];
}

// ---------- the plan ----------

declare const ISO_DATE: unique symbol;

/**
 * An ISO `yyyy-MM-dd` date that has been validated.
 *
 * Branded, so a UK-format string cannot reach a field expecting ISO. This
 * project has shipped a US-format date to a real customer before; the compiler
 * now refuses the mix rather than relying on discipline. `isoDate()` in
 * `calendar.ts` is the only way to make one.
 */
export type IsoDate = string & { readonly [ISO_DATE]: true };

export interface PlannedDinner {
  date: IsoDate;
  mealId: string;
  /** 1 = one night, 2 = cooks double, 3 = batch. */
  spansDays: number;
  /**
   * Portions to cook, overriding the household total. Null means "the whole
   * household" — the normal case. Set it when more people are eating than
   * usual, or fewer.
   */
  portions: number | null;
}

export interface PlannedLunch {
  date: IsoDate;
  personId: string;
  componentIds: string[];
}

export interface PlannedStaple {
  ingredientId: string;
  qty: number;
  unit: Unit;
}

export interface WeekPlan {
  /** Monday of the week, ISO. */
  weekStart: IsoDate;
  store: Store;
  dinners: PlannedDinner[];
  lunches: PlannedLunch[];
  staples: PlannedStaple[];
  /** ingredientId → true when the household already has it. */
  pantry: Record<string, boolean>;
}

// ---------- engine output ----------

/** Demand for one ingredient, already normalised to its base unit. */
export interface Requirement {
  ingredientId: string;
  qty: number;
  /** Human-readable contributors, e.g. "Chilli con carne (Wed)". */
  sources: string[];
}

export interface ShoppingLine {
  /** Null for manually added items with no canonical ingredient. */
  ingredientId: string | null;
  label: string;
  requiredQty: number;
  unit: BaseUnit;
  /** Shelf instruction, e.g. "1kg pack". Null when no pack data exists. */
  packLabel: string | null;
  /** True when pack data was missing and the raw quantity is shown instead. */
  isUnpacked: boolean;
  aisle: AisleCategory;
  sources: string[];
}

/** An extra item's demand for the week. Shown on the list, never pack-rounded. */
export interface ExtraLine {
  /** Stable row and tick key: `extra:<label>|<unit>`, normalised. */
  key: string;
  label: string;
  qty: number | null;
  unitLabel: string;
  aisle: AisleCategory;
  sources: string[];
}
